import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { theme as appTheme } from '../../theme/theme';

const TermsOfServiceScreen: React.FC = () => {
  const navigate = useNavigate();

  // Responsive styles for consistent typography
  const sectionTitleStyles = {
    mb: { xs: 1.5, sm: 2 },
    fontWeight: 'bold',
    color: appTheme.colors.primary,
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    lineHeight: { xs: 1.3, sm: 1.334 },
  };

  const bodyTextStyles = {
    lineHeight: { xs: 1.6, sm: 1.7 },
    fontSize: { xs: '0.875rem', sm: '1rem' },
    textAlign: 'justify' as const,
  };

  const listItemStyles = {
    mb: { xs: 0.5, sm: 1 },
    fontSize: { xs: '0.875rem', sm: '1rem' },
    lineHeight: { xs: 1.5, sm: 1.6 },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--primary-purple) 0%, var(--gradient-purple) 50%, var(--primary-purple) 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>

        <Paper
          elevation={8}
          sx={{
            p: { xs: 2, sm: 3, md: 4, lg: 5 },
            borderRadius: 3,
            backgroundColor: '#FFFFFF',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: { xs: 3, sm: 4 },
              color: appTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
              lineHeight: { xs: 1.2, sm: 1.167 },
            }}
          >
            服务条款
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            最近更新：{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  fontWeight: 'bold', 
                  color: appTheme.colors.primary,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  lineHeight: { xs: 1.3, sm: 1.334 },
                }}
              >
                1. 接受条款
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: { xs: 1.6, sm: 1.7 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: 'justify',
                }}
              >
                访问并使用紫微AI（以下简称“本服务”）即表示您接受并同意受本协议条款与规定的约束；若您不同意上述内容，请勿使用本服务。
              </Typography>
            </Box>

            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  fontWeight: 'bold', 
                  color: appTheme.colors.primary,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  lineHeight: { xs: 1.3, sm: 1.334 },
                }}
              >
                2. 服务描述
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: { xs: 1.6, sm: 1.7 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: 'justify',
                }}
              >
                紫微AI是一个基于中国传统占星学（紫微斗数）提供占星洞见与指导的在线平台。我们的服务包括：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography 
                  component="li" 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                  }}
                >
                  个性化的占星命盘生成与分析
                </Typography>
                <Typography 
                  component="li" 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                  }}
                >
                  年度与月度运势预测
                </Typography>
                <Typography 
                  component="li" 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                  }}
                >
                  合盘分析与关系指导
                </Typography>
                <Typography 
                  component="li" 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                  }}
                >
                  AI 驱动的占星咨询
                </Typography>
                <Typography 
                  component="li" 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                  }}
                >
                  中国占星学相关的教育内容
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                3. 占星服务的性质
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                您理解并确认：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>本服务仅用于娱乐、教育与个人反思目的</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>占星解读不应作为医疗、法律、财务或其他重大决策的专业建议</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>结果与解读基于传统占星原则及 AI 分析，并非科学事实</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>个人结果可能存在差异，且不作保证</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                4. 用户责任
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                作为本服务的用户，您同意：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>提供准确的出生信息用于命盘计算</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>理性使用本服务，不将占星指导作为重大决策的唯一依据</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>尊重我们内容的知识产权</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>不与他人共享您的账户凭证</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>遵守所有适用的法律法规</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                5. 订阅与支付条款
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们的所有服务目前暂时免费，包括但不限于灵魂伴侣画像、运势报告以及AI占星咨询。我们保留未来调整价格或引入收费项目的权利，任何变更都将提前通知用户。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                6. 知识产权
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                紫微AI的所有内容、特性与功能（包括但不限于文本、图形、徽标、图标、图片、音频与软件）均为紫微AI的专有财产，并受版权、商标及其他知识产权法律保护。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                7. 责任限制
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                紫微AI对因您使用或无法使用本服务而导致的任何直接、间接、附带、特殊、后果性或惩罚性损害不承担责任，包括但不限于因利润、商誉、使用、数据或其他无形损失而造成的损害。上述责任限制适用于无论该等损害是否可预见，亦无论基于合同、侵权（含过失）、严格责任或其他法律理论。本服务按“现状”提供，您应自行承担使用风险。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                8. 隐私与数据保护
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们重视您的隐私。请查阅我们的《隐私政策》，其中也会适用于您对本服务的使用，了解我们收集、使用与披露您个人信息的做法。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                9. 条款的修改
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们保留随时修改本条款的权利。对于重大变更，我们将通过电子邮件或服务内通知用户。在此类修改后继续使用本服务，即表示您接受更新后的条款。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                10. 终止
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                如您违反条款，我们可在不另行通知且无需承担责任的情况下立即终止或暂停您的账户与对本服务的访问。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                11. 联系信息
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                如您对本《服务条款》有任何问题，请通过以下方式联系我们：
              </Typography>
              <Box sx={{ 
                mt: { xs: 1.5, sm: 2 }, 
                p: { xs: 1.5, sm: 2 }, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1 
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
            {/* 邮箱已移除，统一以官网为唯一联系渠道 */}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
            <strong>官网：</strong> https://ziwei.jieqirili.com
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServiceScreen;